import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Select, Table, message } from "antd";
import React, { useMemo, useState } from "react";
import { useProject } from "../../App/state";
import {
  useCreateSheetFloorplanMappingMutation,
  useDeleteSheetFloorplanMappingMutation,
  useGetFloorplansQuery,
} from "../../generated/types";
import styles from "./SheetMappingModal.module.css";

interface SiteSheet {
  name: string;
  id: string;
  imageId?: string;
  siteId?: string;
}

interface SheetMappingModalProps {
  visible: boolean;
  onClose: () => void;
  siteSheets: SiteSheet[];
}

export const SheetMappingModal: React.FC<SheetMappingModalProps> = ({
  visible,
  onClose,
  siteSheets,
}) => {
  const { customer, project, scope } = useProject();
  const [selectedSheet, setSelectedSheet] = useState<string | undefined>(
    undefined,
  );
  const [selectedFloorplan, setSelectedFloorplan] = useState<
    string | undefined
  >(undefined);
  const [searchText, setSearchText] = useState<string>("");

  const { data: floorplansData, refetch: refetchFloorplans } =
    useGetFloorplansQuery({
      variables: { customer, project, scope },
      skip: !visible,
    });

  const [createMapping, { loading: createLoading }] =
    useCreateSheetFloorplanMappingMutation();
  const [deleteMapping, { loading: deleteLoading }] =
    useDeleteSheetFloorplanMappingMutation();

  const floorplans = useMemo(
    () => floorplansData?.floorplans || [],
    [floorplansData?.floorplans],
  );

  const handleCreateMapping = async () => {
    if (!selectedSheet || !selectedFloorplan) {
      message.error("Please select both a sheet and a floorplan");
      return;
    }

    const sheet = siteSheets.find((s) => s.id === selectedSheet);
    try {
      await createMapping({
        variables: {
          customer,
          project,
          scope,
          floorplanId: selectedFloorplan,
          osSheetId: selectedSheet,
          imageId: sheet?.imageId,
          siteId: sheet?.siteId,
        },
      });
      message.success("Mapping created successfully");
      setSelectedSheet(undefined);
      setSelectedFloorplan(undefined);
      refetchFloorplans();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      message.error(`Failed to create mapping: ${errorMessage}`);
    }
  };

  const handleDeleteMapping = async (floorplanId: string, sheetId: string) => {
    try {
      await deleteMapping({
        variables: {
          customer,
          project,
          scope,
          floorplanId,
          osSheetId: sheetId,
        },
      });
      message.success("Mapping deleted successfully");
      refetchFloorplans();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      message.error(`Failed to delete mapping: ${errorMessage}`);
    }
  };

  const allMappings = useMemo(() => {
    return floorplans
      .filter((fp) => fp.osSheetMappings && fp.osSheetMappings.length > 0)
      .flatMap((fp) => {
        const fullName =
          fp.space.ancestors && fp.space.ancestors.length > 0
            ? `${fp.space.name} - ${fp.space.ancestors
                .map((a) => a.name)
                .join("-")}`
            : fp.space.name;
        return (fp.osSheetMappings || []).map((mapping) => ({
          key: `${fp.id}-${mapping.osSheetId}`,
          floorplanId: fp.id,
          floorplanName: fullName,
          sheetId: mapping.osSheetId,
          sheetName:
            siteSheets.find((s) => s.id === mapping.osSheetId)?.name ||
            mapping.osSheetId,
        }));
      });
  }, [floorplans, siteSheets]);

  const existingMappings = useMemo(() => {
    if (!searchText.trim()) {
      return allMappings;
    }
    const lowerSearch = searchText.toLowerCase();
    return allMappings.filter(
      (mapping) =>
        mapping.floorplanName.toLowerCase().includes(lowerSearch) ||
        mapping.sheetName.toLowerCase().includes(lowerSearch),
    );
  }, [allMappings, searchText]);

  const columns = [
    {
      title: "Floorplan",
      dataIndex: "floorplanName",
      key: "floorplanName",
    },
    {
      title: "Sheet",
      dataIndex: "sheetName",
      key: "sheetName",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            handleDeleteMapping(record.floorplanId, record.sheetId)
          }
          loading={deleteLoading}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Create/Edit Sheet Mappings"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <div className={styles.createSection}>
        <h3>Create New Mapping</h3>
        <div className={styles.formRow}>
          <Select
            className={styles.selectInput}
            placeholder="Select Sheet"
            value={selectedSheet}
            onChange={setSelectedSheet}
            showSearch
            optionFilterProp="children"
            dropdownClassName={styles.dropdown}
          >
            {siteSheets.map((sheet) => (
              <Select.Option key={sheet.id} value={sheet.id} title={sheet.name}>
                {sheet.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            className={styles.selectInput}
            placeholder="Select Floorplan"
            value={selectedFloorplan}
            onChange={setSelectedFloorplan}
            showSearch
            filterOption={(input, option) => {
              const fullName =
                floorplans.find((fp) => fp.id === option?.value)?.space.name ||
                "";
              const ancestors =
                floorplans
                  .find((fp) => fp.id === option?.value)
                  ?.space.ancestors?.map((a) => a.name)
                  .join("-") || "";
              const searchableText = `${fullName} ${ancestors}`.toLowerCase();
              return searchableText.includes(input.toLowerCase());
            }}
            dropdownClassName={styles.dropdown}
          >
            {floorplans.map((fp) => {
              const fullName =
                fp.space.ancestors && fp.space.ancestors.length > 0
                  ? `${fp.space.name} - ${fp.space.ancestors
                      .map((a) => a.name)
                      .join("-")}`
                  : fp.space.name;
              return (
                <Select.Option key={fp.id} value={fp.id} title={fullName}>
                  <div className={styles.optionText}>{fullName}</div>
                </Select.Option>
              );
            })}
          </Select>
          <Button
            type="primary"
            onClick={handleCreateMapping}
            loading={createLoading}
            disabled={!selectedSheet || !selectedFloorplan}
          >
            Map
          </Button>
        </div>
      </div>

      <div>
        <div className={styles.mappingsHeader}>
          <h3 className={styles.mappingsTitle}>
            Existing Mappings ({existingMappings.length})
          </h3>
          <Input
            placeholder="Search mappings..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
        </div>
        <Table
          columns={columns}
          dataSource={existingMappings}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </div>
    </Modal>
  );
};
